import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";

import { requests } from "../../../common/RestAPI";
import { base64Encoding, getTimestampToDate } from "../../../common/util";
import { SESSIONSTORAGE, FONT } from "../../../value/staticValues";
import { PAGE_URL, PLATFORM_URL } from "../../../value/Url";
import { ALERTS, ERROR_CODE, POPUP_TYPE } from "../../../value/type";

import { ValidationError } from "../../../common/ExceptionError";

import { DetailProps } from "../../../interfaceTypes/ContentsProps";
import { useTranslation } from "react-i18next";
import { PopupModalItemType } from "../../base/modal/PopupModal";
import { VolumeData, snapshotAddModalContent, snapshotUpdateModalContent, vmImageUploadModalContent, volumeUpdateModalContent } from "./VolumeData";
import VolumeDetail from "./VolumeDetail";


export interface AttachedVm {
    vmId: string;
    vmName: string;
    device: string;
};

export interface VolumeSnapshot {
    volumeSnapshotId: string;
    volumeSnapshotName: string;
    volumeSnapshotStatusCode: string;
    volumeSizeGb: string;
    createDate: string;
    updateDate: string;
}


export default function VmDetailContainter(props:DetailProps) {
    const {t} = useTranslation();
    const [infoText, setInfoText] = useState('');
    const [infoError, setInfoError] = useState(false);

    const [targetId, setTargetId] = useState(props.targetId || '');

    const [volumeName, setVolumeName] = useState("");
    const [volumeDesc, setVolumeDesc] = useState("");
    const [bootYn, setBootYn] = useState(false);
    const [refreshYn, setRefreshYn] = useState(false);
    const [executeDeleteVolumeSnapshotId, setExecuteDeleteVolumeSnapshotId] = useState("");
    const [detailData, setDetailData] = useState<VolumeData>();
    const navigate = useNavigate();


    useEffect(() => {
        props.setLoading(false);
        AlertFunction('');
    }, []);


    function AlertFunction(text:string, error=false) {
        setInfoText(text);
        setInfoError(error);
    }


    useEffect(() => {
        if(!targetId) {
            const localTargetId = sessionStorage.getItem(SESSIONSTORAGE.VOLUME_TARGET_ID);
            if(localTargetId) {
                setTargetId(localTargetId);
            } else {
                navigate(`${PAGE_URL.ERRORPAGE}`);
            }
        } else {
            sessionStorage.setItem(SESSIONSTORAGE.VOLUME_TARGET_ID, targetId);
        }
    }, [props]);


    useEffect(() => {
        if(targetId) {
            executeLoadVolume();
        }
    }, [targetId]);


    useEffect(() => {
        if(targetId && refreshYn) {
            executeLoadVolume();
        }
    }, [refreshYn]);


    useEffect(() => {
        if(executeDeleteVolumeSnapshotId) {
            executeDeleteVolumeSnapshot();
        }

    }, [executeDeleteVolumeSnapshotId]);


    function executeDeleteVolumeSnapshot() {
        props.setLoading(true);
        requests.delete(PLATFORM_URL.VOLUME + "/" + targetId + '/volumesnapshots/' + executeDeleteVolumeSnapshotId)
        .then((response) => {
            props.setLoading(false);
            setExecuteDeleteVolumeSnapshotId("");
            deleteSnapshotResult({successYn: true});
        })
        .catch((error) => {
            props.setLoading(false);
            setExecuteDeleteVolumeSnapshotId("");
            deleteSnapshotResult({successYn: false});
        })
    }


    function executeLoadVolume() {
        props.setLoading(true);
        requests.get(PLATFORM_URL.VOLUME + "/" + targetId)
        .then((response) => {
            if (response.status === 200 && response.data) {
                setDetailData(response.data);
            }
            setRefreshYn(false);
            props.setLoading(false);
        })
        .catch((error) => {
            //show error message
            AlertFunction(t("MESSAGE_CANNOT_LOAD_DATA_MESSAGE"), true);
            setRefreshYn(false);
            props.setLoading(false);
        });
    }

    function addSnapshotResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VOLUME_SNAPSHOT_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function updateVolumeResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VOLUME_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }
    function updateSnapshotResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VOLUME_SNAPSHOT_UPDATE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }

    function deleteSnapshotResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VOLUME_SNAPSHPT_DELETE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        } else {
            AlertFunction(t("MESSAGE_VOLUME_SNAPSHPT_DELETE_FAIL_MESSAGE"));
        }
    }

    function uploadVmImageResult(data) {
        if(data.successYn) {
            AlertFunction(t("MESSAGE_VOLUME_UPLOAD_VM_IMAGE_SUCCESS_MESSAGE"));
            setRefreshYn(true);
        }
    }


    function clickVolumeUpdate() {
        const data = {
            volumeId: targetId,
            volumeName: volumeName,
            volumeDesc: volumeDesc,
            bootYn: bootYn
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...volumeUpdateModalContent, openYn: true, resultCb: updateVolumeResult, args: detailData});
        }
    }

    function clickSnapshotAdd() {
        const data = {
            volumeId: targetId,
            volumeName: volumeName,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...snapshotAddModalContent, openYn: true, resultCb: addSnapshotResult, args: data});
        }
    }

    function clickSnapshotUpdate(item) {
        const data = {
            volumeId: targetId,
            volumeSnapshotId: item.volume_snapshot_id,
            volumeSnapshotName: item.volume_snapshot_name
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...snapshotUpdateModalContent, openYn: true, resultCb: updateSnapshotResult, args: data});
        }
    }

    function clickVmImageUpload() {
        const data = {
            volumeId: targetId,
            volumeName: volumeName,
        }
        if(props.showSlideModalCb) {
            props.showSlideModalCb({...vmImageUploadModalContent, openYn: true, resultCb: uploadVmImageResult, args: detailData});
        }
    }


    function clickSnapshotDelete(item) {
        props.showPopupModalCb({openYn: true,
            type: POPUP_TYPE.MESSAGE_WITH_TITLE_POPUP,
            title: t("MESSAGE_VOLUME_SNAPSHOT_DELETE_REQUEST_TITLE_MESSAGE"),
            message: t("MESSAGE_VOLUME_SNAPSHOT_DELETE_REQUEST_MESSAGE"),
            code: "",
            cancelButtonYn: true,
            confirmButtonCb: (code) => {setExecuteDeleteVolumeSnapshotId(item.volume_snapshot_id)},
        });
    }


    return (
            targetId ?
            <VolumeDetail
                infoText={infoText}
                infoError={infoError}
                updateVolumeFunction={clickVolumeUpdate}
                uploadVmImageFunction={clickVmImageUpload}
                addSnapshotFunction={clickSnapshotAdd}
                deleteSnapshotFunction={clickSnapshotDelete}
                updateSnapshotFunction={clickSnapshotUpdate}
                refreshYn={refreshYn}
                detailData={detailData ? detailData : null}
            />
            : null
    );
}
